/**
 * Mouvement AGORA (règle du 26/10/2026 : des règles plutôt que des effets au cas par cas – web/README.md › Mouvement).
 *
 * - Jetons : trois durées (courte, moyenne, longue) et trois courbes (standard, entrée, sortie), générés depuis
 *   design/design-system/tokens.json. En CSS : var(--duration-*), var(--ease-*) ; en JavaScript : DUREES, COURBES
 *   (courbeCss() pour element.animate). Au-delà de la durée longue, une chorégraphie s'exprime en multiples de la
 *   durée longue (`calc(var(--duration-long) * 2)`, `DUREES.long * 2`).
 * - « Réduire les animations » (réglage du système) : les durées CSS tombent à presque rien (tokens.css),
 *   mouvementReduit() le dit au JavaScript, Motion est configuré en reducedMotion="user" (motion/zone-motion.ts).
 * - Motion (bibliothèque d'animation) : réservée aux cas complexes. Jamais importée par le bundle principal : un
 *   composant animé par Motion est un module chargé à la demande (React.lazy) qui enveloppe ses `m.*` dans
 *   <ZoneMotion> – modèle : motion/pastille-motion.ts (pastille glissante du choix segmenté).
 */
import { motionTokens } from './styles/generated/tokens';

/** Durées des jetons, en millisecondes : short 150, medium 200, long 320. */
export const DUREES = motionTokens.duration;
/** Courbes des jetons (points de cubic-bezier, format de Motion) : standard, enter, exit. */
export const COURBES = motionTokens.ease;
export type Duree = keyof typeof DUREES;
export type Courbe = keyof typeof COURBES;

/** Courbe d'un jeton au format CSS (`cubic-bezier(…)`), pour l'API Web Animations (element.animate). */
export function courbeCss(courbe: Courbe): string {
  return `cubic-bezier(${COURBES[courbe].join(', ')})`;
}

/** Réglage du système « réduire les animations », lu au moment de l'appel. */
export function mouvementReduit(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Transition Motion construite sur les jetons ; aucune durée si l'utilisateur a réduit les animations. */
export function transitionMotion(duree: Duree, courbe: Courbe, reduit: boolean | null): { duration: number; ease?: readonly [number, number, number, number] } {
  return reduit ? { duration: 0 } : { duration: DUREES[duree] / 1000, ease: COURBES[courbe] };
}
